/**
 * WhatHappensNext.tsx — the explainer beat between the qualification form and
 * the questionnaire (CTO, 2026-09-12).
 *
 * Owner repo: @thegpservice/gpsclinics-portal.
 *
 * WHY IT EXISTS. The questionnaire is thirteen questions long and asks for a
 * date of birth, a set of conditions and a photograph before it says what any
 * of it is for. This screen is the answer to "why am I filling this in": who
 * reads it, when money moves, and what turns up if a clinician says yes.
 *
 * ⚠️ PRE-ELIGIBILITY PUBLIC SURFACE — CATEGORY-LEVEL COPY ONLY (R28). No
 * visitor reaching this screen has passed any eligibility gate, so nothing here
 * may name a prescription-only medicine, a brand, a molecule, a strength or a
 * dose form. "Treatment" is the ceiling, and it is the same ceiling Step 0 and
 * the projection interstitial work under. `r28.spec.tsx` scans this file's
 * markup and its source along with the rest of `src/`.
 *
 * ⚠️ AND IT MAKES NO PRICE COMPARISON. The CTO's sketch put Switch & Save
 * between the qualification form and this screen. It is NOT here, and that is
 * deliberate: Switch & Save is a comparative price claim whose only compliance
 * cover is that it renders beside the questionnaire's prior-treatment question,
 * which clinic-service serves only AFTER the eligibility gate (see
 * `switchAndSaveCopy.ts`). Promoting it to this screen would put a "we cost
 * 8–10% less than other UK online clinics" claim onto a public marketing
 * surface with nothing in front of it. It stays where it is, unchanged, and is
 * still shown to exactly the patients it is written for — switchers — one
 * screen-set later.
 *
 * ⚠️ 2026-09-25 (CTO): the journey ENTRY loader's content card now shows the
 * Switch & Save claim (the name and the saving's short form, figure, qualifier
 * and comparator) to every fresh start (`entryLoaderContent.ts`) — a public,
 * pre-gate placement, at the CTO's request. The reasoning above is
 * kept because it is still the open question: the sentence names no medicine
 * (R28 is not the issue — the prior-treatment QUESTION's options are why that
 * screen is post-gate), but the CAP Code 3.7 evidence for the 8–10% is still
 * not held, and the loader is its widest placement yet (CLAUDE.md, Switch &
 * Save — an open CTO item). This screen still does not show it.
 *
 * ── EVERY CLAIM BELOW IS ALREADY SHIPPED COPY ───────────────────────────────
 * The timings and the money wording are lifted from `checkout/paymentCopy.ts`
 * (the hold route's payment + confirmation copy), not invented for this screen.
 * That matters twice over: a promise made here and contradicted at checkout is
 * the kind of thing that ends up in a complaint, and a delivery or review SLA
 * nobody signed off is a claim we cannot substantiate. If the checkout copy
 * changes, change this with it.
 *
 * ── SHORT, PLAIN AND ICON-LED (Ashley Haveloch-Jones, via the CTO, 2026-09-26) ─
 * "Condense the text, it's too much" — on a phone the old four paragraphs ran
 * past the fold before the button. The CTO: "don't lose the messaging but
 * condense it, use icons more where ever possible", and "No charge until your
 * treatment is approved." is important — so it is no longer a grey help line
 * but the highlighted line at the top. Every step still says what it said (the
 * questions are our clinicians'; a hold, not a payment; a UK clinician's review
 * and its one timing; a GPhC-registered pharmacy) in about a third of the
 * words, and in the patient's words rather than ours: never "we take payment"
 * (Ashley: phrases like that "never land with a customer"). "Not approved? You
 * pay nothing." is the highlighted line's own promise, so it is not repeated
 * under step 4. The patient-copy rules are in CLAUDE.md ("Patient copy — how we
 * write").
 *
 * THE LOADER'S LOOK, ONE LINE A STEP (the CTO, the same day, on the first cut:
 * "remove the subtext under main point and connect the content there with the
 * main point and have it with the image like we have in the loader"). The steps
 * are the entry loader's own `LoaderJourneyCard`, `still` (nothing grows in —
 * this screen waits on nothing), so the two cards cannot drift: icon discs, a
 * dotted rail and a row of four at ≥880px, a column on a phone. Each step is
 * ONE sentence — its headline in bold, run straight on into the rest
 * (`runIn`) — never a headline over a grey subtext.
 *
 * The hold framing is right for THIS lane specifically: the self-serve
 * questionnaire path always takes the pre-authorisation route. The immediate-
 * capture route belongs to the clinician-led lane, which enters checkout from a
 * plan link and never renders this screen.
 */
import { LoaderJourneyCard, type LoaderJourney, type LoaderJourneyIcon } from '../ui/LoaderJourneyCard';
import { Eyebrow, Title } from '../ui/kit';
import { JourneyButton } from './JourneyUi';
import { JourneyScreen } from './JourneyScreen';
import { NoChargeLine } from './NoChargeLine';
import { CLINICIAN_REVIEWS_ASSESSMENT, REVIEW_USUALLY_WITHIN_ONE_WORKING_DAY } from './serviceClaims';

/**
 * Exported so the compliance spec can scan the words, not just the markup — and,
 * since 2026-09-25, because the entry loader card's journey ("Here's how it
 * works") restates these four steps in SHORT FORMS (`entryLoaderContent.ts`,
 * `serviceClaims.ts`): "A few health questions", "Choose a plan, add a card"
 * and "Usually within one working day" are pinned by `entryLoaderContent.spec`
 * against the words here, so a reorder or reword of a step turns it red until
 * the loader's short form is re-checked. Step 4's "Approved? Sent to your door"
 * takes its words from the celebration's hold trust point
 * (`checkout/celebrationCopy.ts`) and is pinned there; the spec also holds this
 * title to its "If approved, …" condition.
 *
 * `title` is the bold headline and `body` the words that run straight on from
 * it in the same sentence (rendered as the card's `runIn`).
 *
 * ⚠️ WHAT EACH STEP STILL HAS TO SAY (2026-09-26 cut):
 *   1. the questions are our clinicians'.
 *   2. a HOLD goes on the card — never "free" or "£0" — and nothing is paid yet.
 *   3. a UK clinician reviews, "usually within one working day" (the site's one
 *      review timing, "usually" kept).
 *   4. the condition ("If approved") and the GPhC-registered pharmacy.
 * The money TIMING — and so "not approved, you pay nothing" — is the screen's
 * highlighted line ("No charge until your treatment is approved."), above the
 * steps, not repeated in them.
 */
export const WHAT_HAPPENS_NEXT_STEPS: ReadonlyArray<{ icon: LoaderJourneyIcon; title: string; body: string }> = [
  { icon: 'choose', title: 'Answer a few health questions', body: 'from our clinicians' },
  { icon: 'card', title: 'Choose a plan and add a card', body: '— it’s only a hold, you don’t pay yet' },
  {
    // One constant with serviceClaims.ts, whose short form ("UK clinician review") the entry loader card shows.
    icon: 'clinician',
    title: CLINICIAN_REVIEWS_ASSESSMENT,
    body: REVIEW_USUALLY_WITHIN_ONE_WORKING_DAY.replace(/^U/, 'u'),
  },
  { icon: 'parcel', title: 'If approved, we send your treatment', body: 'from a GPhC-registered pharmacy' },
];

/**
 * The card, as `LoaderJourneyCard` takes it. A module constant, so a re-render
 * never hands the card a new object. The heading names the region for a screen
 * reader; the page's own title says it on screen.
 */
const WHAT_HAPPENS_NEXT_CARD: LoaderJourney = {
  heading: 'The four steps',
  steps: WHAT_HAPPENS_NEXT_STEPS.map((step) => ({ id: step.icon, icon: step.icon, title: step.title, runIn: step.body })),
};

export function WhatHappensNext({ percent, onBack, onContinue }: { percent?: number; onBack: () => void; onContinue: () => void }) {
  return (
    <JourneyScreen
      onBack={onBack}
      percent={percent}
      action={
        <JourneyButton type="button" variant="primary" arrow onClick={onContinue}>
          Start my questions
        </JourneyButton>
      }
    >
      <Eyebrow>What happens next</Eyebrow>
      <Title>Four simple steps</Title>
      {/* THE LINE THE CTO CALLED IMPORTANT, highlighted rather than greyed: it is
          the answer to the question every patient has before handing over a
          card. Its words are Step 0's, from one constant, in the one highlighted
          box the plan screen shows under its button too (`NoChargeLine`). */}
      <NoChargeLine className="gps-whn-no-charge" />
      <LoaderJourneyCard journey={WHAT_HAPPENS_NEXT_CARD} reduced still headingHidden />
    </JourneyScreen>
  );
}
